import React, { memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Cpu, Heart, Edit, Camera, Skull, ArrowLeftRight, Eye, Globe, EyeOff, Trash2 
} from 'lucide-react';

const AnimalCard = memo(({ 
  animal, 
  onViewDetails, 
  onEdit, 
  onAdopt, 
  onTransfer, 
  onReportDeath, 
  onUploadPhoto,
  onPublishToWebsite,
  onUnpublishFromWebsite,
  onDelete
}) => {
  const getStatusBadge = (status) => {
    const statusColors = {
      'available': 'bg-green-100 text-green-800',
      'adopted': 'bg-blue-100 text-blue-800',
      'fostered': 'bg-purple-100 text-purple-800',
      'medical_hold': 'bg-yellow-100 text-yellow-800',
      'quarantine': 'bg-red-100 text-red-800',
      'pending_adoption': 'bg-orange-100 text-orange-800',
      'transferred': 'bg-gray-100 text-gray-800',
      'deceased': 'bg-stone-100 text-stone-800'
    };
    return statusColors[status] || 'bg-stone-100 text-stone-600';
  };

  const isDeceased = animal.status === 'deceased';
  const isAdopted = animal.status === 'adopted';
  const isTransferred = animal.status === 'transferred';
  const canBeAdopted = ['available', 'fostered'].includes(animal.status);
  const canBeTransferred = ['available', 'fostered', 'medical_hold'].includes(animal.status);

  return (
    <Card className={`hover:shadow-md transition-shadow ${isDeceased ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Photo */}
          <div className="w-24 h-24 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {animal.photos && animal.photos.length > 0 ? (
              <img 
                src={animal.photos[0]} 
                alt={animal.name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl">
                {animal.species === 'Dog' ? '🐕' : animal.species === 'Cat' ? '🐈' : '🐾'}
              </span>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-lg truncate">{animal.name}</h3>
                <p className="text-sm text-stone-600">
                  {animal.species} • {animal.breed || 'Unknown breed'} • {animal.gender}
                </p>
              </div>
              <Badge className={getStatusBadge(animal.status)}>
                {animal.status?.replace('_', ' ')}
              </Badge>
            </div>
            
            {animal.microchip_number && (
              <p className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                <Cpu className="w-3 h-3" /> {animal.microchip_number}
              </p>
            )}
            
            {animal.published_to_website && (
              <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">
                <Globe className="w-3 h-3 mr-1" />
                Published ({animal.website_publish_type || 'adoptable'})
              </Badge>
            )}
            
            {animal.adoption_fee && !isDeceased && !isAdopted && (
              <p className="text-sm text-primary font-medium mt-1">
                Adoption Fee: ${animal.adoption_fee}
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onViewDetails(animal)}
              >
                <Eye className="w-3 h-3 mr-1" /> View
              </Button>
              
              {!isDeceased && !isAdopted && !isTransferred && (
                <>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onEdit(animal)}
                  >
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onUploadPhoto(animal)}
                  >
                    <Camera className="w-3 h-3 mr-1" /> Photo
                  </Button>
                  
                  {canBeAdopted && (
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => onAdopt(animal)}
                    >
                      <Heart className="w-3 h-3 mr-1" /> Adopt
                    </Button>
                  )}
                  
                  {canBeTransferred && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onTransfer(animal)}
                    >
                      <ArrowLeftRight className="w-3 h-3 mr-1" /> Transfer
                    </Button>
                  )}
                  
                  {/* Publish/Unpublish to Website */}
                  {canBeAdopted && !animal.published_to_website && onPublishToWebsite && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-blue-600 hover:bg-blue-50"
                      onClick={() => onPublishToWebsite(animal)}
                    >
                      <Globe className="w-3 h-3 mr-1" /> Publish
                    </Button>
                  )}
                  
                  {animal.published_to_website && onUnpublishFromWebsite && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-orange-600 hover:bg-orange-50"
                      onClick={() => onUnpublishFromWebsite(animal)}
                    >
                      <EyeOff className="w-3 h-3 mr-1" /> Unpublish
                    </Button>
                  )}
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => onReportDeath(animal)}
                  >
                    <Skull className="w-3 h-3 mr-1" /> Report Death
                  </Button>
                  
                  {onDelete && (
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => onDelete(animal)}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

AnimalCard.displayName = 'AnimalCard';

export default AnimalCard;
